import { DeleteIcon } from '@/components/icons'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useDeleteChat } from '@/hooks'
import type { Chat } from '@/types'
import { X } from 'lucide-react'
import { toast } from 'sonner'

interface DeleteChatDialogProps {
  projectId: string
  chat: Chat
  open: boolean
  onOpenChange: (open: boolean) => void
  onDeleted: (chatId: string) => void
}

export function DeleteChatDialog({ projectId, chat, open, onOpenChange, onDeleted }: DeleteChatDialogProps) {
  const deleteChat = useDeleteChat(projectId)

  const handleDelete = async () => {
    try {
      await deleteChat.mutateAsync(chat.id)
      toast.success('Chat deleted successfully')
      onDeleted(chat.id)
      onOpenChange(false)
    } catch {
      toast.error('Failed to delete chat')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[640px] rounded-2xl p-8 gap-6 border-none shadow-elevated" hideCloseButton>
        <DialogHeader className="flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-2xl font-bold leading-[1.3] tracking-[0.015em] text-foreground">Delete Chat?</DialogTitle>
          <DialogClose className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none">
            <X className="h-6 w-6" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>
        <DialogDescription className="text-sm font-normal leading-[1.6] tracking-[0.015em] text-foreground">
          Are you sure you want to delete the chat "<span className="font-bold">{chat.title}</span>"? This action
          cannot be undone.
        </DialogDescription>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={deleteChat.isPending}
            className="h-10 min-w-10 rounded-lg py-2.5 px-4 gap-2.5 font-bold"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteChat.isPending}
            data-testid="delete-chat-confirm-button"
            className="h-10 min-w-10 rounded-lg py-2.5 px-4 gap-2.5 font-bold [&_svg]:size-6"
          >
            <DeleteIcon className="h-6 w-6" />
            {deleteChat.isPending ? 'Deleting...' : 'Delete Chat'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
